// screens/FeedbackScreen.js
// Photographer-facing feedback: photos a reviewer rejected, grouped by order,
// with the reviewer's note and a one-tap "re-shoot" that opens a fresh session.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useLang } from '../contexts/LanguageContext';
import { SyncBar, PhotoThumb, EmptyState } from '../components/shared';
import PhotoViewer from '../components/PhotoViewer';
import { BottomNav } from './HomeScreen';
import api from '../utils/api';

export default function FeedbackScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useLang();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerPhoto, setViewerPhoto] = useState(null);
  const [reshooting, setReshooting] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/photos/feedback');
      setPhotos(data || []);
    } catch { toast.error('Could not load feedback'); }
    finally { setLoading(false); }
  };

  const markDone = async (photoId) => {
    // Optimistically remove the line; restore on failure.
    const prev = photos;
    setPhotos((curr) => curr.filter((p) => p.id !== photoId));
    try {
      await api.post(`/api/photos/${photoId}/feedback-done`);
      toast.success(t('feedback.markeddone'));
    } catch {
      setPhotos(prev);
      toast.error('Could not mark as done');
    }
  };

  const reshoot = async (orderId) => {
    setReshooting(orderId);
    try {
      const { data } = await api.post('/api/sessions', { order_id: orderId });
      navigate(`/guided-photo/${orderId}/${data.id}`);
    } catch {
      toast.error('Could not start a re-shoot session');
      setReshooting(null);
    }
  };

  // Group rejected photos by order.
  const groups = [];
  for (const p of photos) {
    let g = groups.find(x => x.order_id === p.order_id);
    if (!g) { g = { order_id: p.order_id, order_number: p.order_number, product_name: p.product_name, items: [] }; groups.push(g); }
    g.items.push(p);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', minHeight: '100dvh' }}>
      <SyncBar />
      <div className="page-header">
        <button className="btn btn-ghost btn-icon" onClick={() => navigate('/')}>←</button>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: '1.125rem' }}>{t('feedback.title')}</h2>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {photos.length === 0 ? t('feedback.none') : `${photos.length} ${t('feedback.toreshoot')}`}
          </div>
        </div>
        <button className="btn btn-ghost btn-icon" onClick={load}>↻</button>
      </div>

      <div className="page-content">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner spinner-lg" /></div>
        ) : groups.length === 0 ? (
          <EmptyState icon="✅" title={t('feedback.allclear')} message={t('feedback.allclear.msg')} />
        ) : (
          groups.map(g => (
            <div key={g.order_id} className="card" style={{ marginBottom: 16, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent-primary)' }}>{g.order_number}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.product_name}</div>
                </div>
                <button className="btn btn-primary btn-sm" disabled={reshooting === g.order_id}
                  onClick={() => reshoot(g.order_id)}>
                  {reshooting === g.order_id ? '…' : `↻ ${t('feedback.reshoot')}`}
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {g.items.map(p => (
                  <div key={p.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ width: 84, flexShrink: 0 }}>
                      <PhotoThumb photo={p} onClick={() => setViewerPhoto(p)} showLabel={false} />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.step_name}</div>
                      <div style={{
                        marginTop: 4, padding: '8px 10px', borderRadius: 8,
                        background: 'rgba(244,88,88,0.12)', border: '1px solid rgba(244,88,88,0.35)',
                        fontSize: '0.8125rem', color: 'var(--text-primary)',
                      }}>
                        <span style={{ color: '#f45858', fontWeight: 700 }}>✗ {t('feedback.rejected')}</span>
                        {p.review_note ? ` — ${p.review_note}` : ` — ${t('feedback.nonote')}`}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 6 }}>
                        <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                          {p.reviewer_name || 'Reviewer'}{p.reviewed_at ? ` · ${new Date(p.reviewed_at).toLocaleString()}` : ''}
                        </div>
                        <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0, color: 'var(--accent-success)' }}
                          onClick={() => markDone(p.id)}>
                          ✓ {t('feedback.done')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {viewerPhoto && (
        <PhotoViewer photo={viewerPhoto} photos={photos} onClose={() => setViewerPhoto(null)} />
      )}

      <BottomNav active="feedback" />
    </div>
  );
}
